import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { ShellContext } from './shellContext';
import { CUSTOMER_ITEMS } from './nav';
import { CommandPaletteProvider } from '@/components/palette/CommandPalette';
import { TimeRangeProvider } from '@/context/TimeRangeContext';
import useKeyboardShortcuts, { prettyShortcut } from '@/hooks/useKeyboardShortcuts';
import useMediaQuery from '@/hooks/useMediaQuery';
import './shell.css';

const COLLAPSE_KEY = 'is.sidebar.collapsed';
const readCollapsed = () => { try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; } };

function GlobalKeys({ variant, onShortcuts }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The threat queue has its own, fuller shortcut sheet on "?".
  const ownHelp = pathname.startsWith('/threats');
  const map = useMemo(() => {
    const m = ownHelp ? {} : { 'shift+?': onShortcuts, '?': onShortcuts };
    if (variant !== 'admin') CUSTOMER_ITEMS.forEach((i) => { if (i.keys) m[`g ${i.keys}`] = () => navigate(i.to); });
    return m;
  }, [navigate, onShortcuts, variant, ownHelp]);
  useKeyboardShortcuts(map);
  return null;
}

function ShortcutsDialog({ open, onOpenChange }) {
  const go = CUSTOMER_ITEMS.filter((i) => i.keys).map((i) => [`Go to ${i.label}`, `G then ${i.keys.toUpperCase()}`]);
  const general = [['Search or run a command', prettyShortcut('mod+k')], ['Show this list', '?'], ['Close a panel or dialog', 'Esc']];
  const queue = [['Next or previous threat', 'J / K'], ['Filter the queue', '/'], ['Start triage', 'T'], ['Acknowledge', 'A'], ['Archive', 'Shift A']];
  const Section = ({ title, rows }) => (
    <section>
      <h3 className="sh-keys-h">{title}</h3>
      <dl className="sh-keys">{rows.map(([l, k]) => <div key={l}><dt>{l}</dt><dd><kbd>{k}</kbd></dd></div>)}</dl>
    </section>
  );
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="sh-scrim" />
        <Dialog.Content className="sh-dialog" aria-describedby={undefined}>
          <div className="sh-dialog-head">
            <Dialog.Title className="sh-dialog-title">Keyboard shortcuts</Dialog.Title>
            <Dialog.Close className="sh-icon-btn" aria-label="Close"><X className="sh-ic" aria-hidden="true" /></Dialog.Close>
          </div>
          <div className="sh-dialog-body sh-keys-grid">
            <Section title="Everywhere" rows={general} />
            <Section title="Threat queue" rows={queue} />
            <Section title="Navigation" rows={go} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Signed-in app frame: labeled sidebar, a 48px context bar, and one scrolling
 * work area (<main>). Pages that need their own scrolling panes (the threat
 * queue) set `flush` and fill the height.
 *
 *   flush     no padding on main (pages that manage their own layout)
 *   variant   'customer' | 'admin' (admin nav, admin badge)
 */
export default function AppShell({ children, variant = 'customer', flush = false }) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const isPhone = useMediaQuery('(max-width: 767.98px)');
  const [navOpen, setNavOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => {
    try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch { /* remember for this session only */ }
    return !c;
  }), []);
  const openShortcuts = useCallback(() => setShortcutsOpen(true), []);
  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => { setNavOpen(false); }, [pathname]);
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const shell = useMemo(() => ({ openNav: () => setNavOpen(true), closeNav, navOpen, openShortcuts }), [closeNav, navOpen, openShortcuts]);

  return (
    <TimeRangeProvider>
      <CommandPaletteProvider>
        <ShellContext.Provider value={shell}>
          <GlobalKeys variant={variant} onShortcuts={openShortcuts} />
          <div data-panel={variant} className={`sh ${collapsed ? 'is-collapsed' : ''}`}>
            <a href="#main" className="sh-skip">Skip to main content</a>
            <Sidebar variant={variant} collapsed={collapsed && !isPhone} onToggleCollapsed={toggleCollapsed} mobileOpen={navOpen} onNavigate={closeNav} />
            {navOpen && <div className="sh-drawer-scrim" onClick={closeNav} aria-hidden="true" />}
            <div className="sh-col">
              <Topbar variant={variant} />
              <main id="main" tabIndex={-1} className={`sh-main ${flush ? '' : 'sh-main--padded'}`}>
                {children ?? <Outlet />}
              </main>
            </div>
          </div>
          <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
        </ShellContext.Provider>
      </CommandPaletteProvider>
    </TimeRangeProvider>
  );
}
